import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';


const EMAIL = 'andrescadena.dg@gmail.com';

export default function Contact() {
  const ref = useScrollAnimation();
  const [copied, setCopied] = useState(false);
  const [showEmailMenu, setShowEmailMenu] = useState(false);

  const handleEmailClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowEmailMenu((prev) => !prev);
  };

  const openMailto = () => {
    window.location.href = `mailto:${EMAIL}`;
    setShowEmailMenu(false);
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);
      setShowEmailMenu(false);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative bg-[#0a0a0a] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#FF6B35]/5 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-[#00D9FF]/5 blur-3xl"></div>
      </div>

      <div ref={ref} className="relative z-[3] max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="fade-in-up flex items-center gap-4 mb-6">
          <span className="text-[#FF6B35] font-bebas text-lg tracking-widest">06</span>
          <div className="w-12 h-px bg-[#FF6B35]"></div>
          <span className="text-white/40 text-sm tracking-widest uppercase">Contacto</span>
        </div>

        {/* CTA Hero */}
        <div className="fade-in-up text-center mb-16">
          <h2 className="font-bebas text-5xl md:text-7xl lg:text-8xl text-white leading-tight mb-4">
            ¿LISTO PARA CREAR
            <br />
            <span className="gradient-text">ALGO INCREÍBLE?</span>
          </h2>
          <p className="text-white/50 font-poppins text-lg max-w-xl mx-auto">
            Hablemos de tu proyecto. Estoy disponible para nuevos trabajos y colaboraciones.
          </p>
        </div>

        {/* Contact buttons */}
        <div className="fade-in-up flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <a
            href="https://wa.me/573024819377?text=Hola%20Michael%2C%20quiero%20hablar%20sobre%20un%20proyecto"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-[#25D366] hover:bg-[#1db954] text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 text-base whitespace-nowrap w-full sm:w-auto justify-center"
          >
            <i className="ri-whatsapp-line text-xl"></i>
            WhatsApp: 3024819377
          </a>
          <div className="relative w-full sm:w-auto">
            <button
              onClick={handleEmailClick}
              className={`flex items-center gap-3 border-2 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 text-base whitespace-nowrap w-full sm:w-auto justify-center cursor-pointer ${
                copied
                  ? 'border-[#06FFA5] bg-[#06FFA5]/10 text-[#06FFA5]'
                  : 'border-white/20 hover:border-white hover:bg-white/10'
              }`}
            >
              <i className={`text-xl ${copied ? 'ri-check-line' : 'ri-mail-line'}`}></i>
              {copied ? '¡Correo copiado!' : EMAIL}
            </button>

            {showEmailMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowEmailMenu(false)}
                ></div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 z-20 bg-[#1a1a1a] border border-white/10 rounded-2xl overflow-hidden shadow-xl min-w-[220px]">
                  <div className="px-4 py-2.5 border-b border-white/10">
                    <p className="text-white/40 text-xs font-poppins">Abrir correo con...</p>
                  </div>
                  <button
                    onClick={openMailto}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/5 hover:text-white transition-colors duration-200 cursor-pointer text-sm font-poppins"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10">
                      <i className="ri-mail-open-line text-white/60 text-sm"></i>
                    </div>
                    Abrir con correo del dispositivo
                  </button>
                  <button
                    onClick={copyEmail}
                    className="flex items-center gap-3 w-full px-4 py-3 text-white/80 hover:bg-white/5 hover:text-white transition-colors duration-200 cursor-pointer text-sm font-poppins border-t border-white/10"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10">
                      <i className="ri-file-copy-line text-white/60 text-sm"></i>
                    </div>
                    Copiar correo
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Info grid */}
        <div className="fade-in-up grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/10 pt-16 max-w-2xl mx-auto">
          {/* Location */}
          <div className="text-center p-8 bg-[#111] rounded-2xl border border-white/5">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#FF6B35]/15 mx-auto mb-4">
              <i className="ri-map-pin-line text-2xl text-[#FF6B35]"></i>
            </div>
            <h4 className="font-poppins font-bold text-white text-lg mb-2">Ubicación</h4>
            <p className="text-white/60 text-sm">Bogotá, Colombia</p>
            <p className="text-white/40 text-xs mt-1">Disponible para proyectos remotos</p>
          </div>

          {/* Social */}
          <div className="text-center p-8 bg-[#111] rounded-2xl border border-white/5">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#00D9FF]/15 mx-auto mb-4">
              <i className="ri-share-line text-2xl text-[#00D9FF]"></i>
            </div>
            <h4 className="font-poppins font-bold text-white text-lg mb-4">Redes Sociales</h4>
            <div className="flex items-center justify-center gap-4">
              {[
                { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://www.instagram.com/andrescadena.dg?igsh=MWNmd3hnM2pmamdmbQ==' },
                { icon: 'ri-facebook-circle-line', label: 'Facebook', href: 'https://www.facebook.com/share/18EJ8NMJXZ/' },
                { icon: 'ri-tiktok-line', label: 'TikTok', href: 'https://www.tiktok.com/@andrescadena.dg?_r=1&_t=ZS-95k00WX4C85' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center gap-1.5"
                  aria-label={s.label}
                >
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-white/5 border border-white/10 transition-all duration-300 group-hover:bg-[#06FFA5]/20 group-hover:border-[#06FFA5]/60 group-hover:scale-110">
                    <i className={`${s.icon} text-lg text-white/70 transition-colors duration-300 group-hover:text-[#06FFA5]`}></i>
                  </div>
                  <span className="text-white/30 text-[10px] transition-colors duration-300 group-hover:text-[#06FFA5]/70">{s.label}</span>
                </a>
              ))}
            </div>
          </div>


        </div>
      </div>
    </section>
  );
}
